import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, GripVertical, DollarSign } from 'lucide-react';
import { costSectionsApi, costItemsApi, locationsApi } from '@/api/client';
import { toast } from '@/utils/toast';

const sectionSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    sectionType: z.enum(['FIXED', 'PERCENTAGE']),
    isRequired: z.boolean().default(true),
});

const itemSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    unit: z.enum(['PER_DAY', 'PER_PROCEDURE', 'PER_UNIT']),
    isSelectable: z.boolean().default(false),
    exclusiveGroup: z.string().optional(),
    defaultAmount: z.number().nullable().optional(),
    defaultPercentage: z.number().nullable().optional(),
    defaultUnitPrice: z.number().nullable().optional(),
}).superRefine((data, ctx) => {
    if (data.unit === 'PER_UNIT' && (data.defaultUnitPrice === null || data.defaultUnitPrice === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Base Unit Price is required",
            path: ["defaultUnitPrice"]
        });
    }
});

type SectionFormData = z.infer<typeof sectionSchema>;
type ItemFormData = z.infer<typeof itemSchema>;

export function CostConfigPage() {
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingSection, setEditingSection] = useState<any>(null);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [selectedSectionId, setSelectedSectionId] = useState<string>('');
    const queryClient = useQueryClient();

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: sections = [], isLoading: sectionsLoading } = useQuery({
        queryKey: ['cost-sections', selectedLocationId],
        queryFn: () => costSectionsApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: items = [], isLoading: itemsLoading } = useQuery({
        queryKey: ['cost-items', selectedSectionId],
        queryFn: () => costItemsApi.list(selectedSectionId),
        enabled: !!selectedSectionId,
    });

    const createSectionMutation = useMutation({
        mutationFn: (data: SectionFormData & { locationId: string }) =>
            costSectionsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-sections'] });
            toast.success('Section created successfully');
            setIsSectionModalOpen(false);
            setEditingSection(null);
            resetSection({
                name: '',
                sectionType: 'FIXED',
                isRequired: true,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create section');
        },
    });

    const updateSectionMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<SectionFormData> }) =>
            costSectionsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-sections'] });
            toast.success('Section updated successfully');
            setIsSectionModalOpen(false);
            setEditingSection(null);
            resetSection({
                name: '',
                sectionType: 'FIXED',
                isRequired: true,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update section');
        },
    });

    const deleteSectionMutation = useMutation({
        mutationFn: costSectionsApi.delete,
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: ['cost-sections'] });
            // If the deleted section was selected, clear the selection
            if (selectedSectionId === deletedId) {
                setSelectedSectionId('');
            }
            toast.success('Section deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to delete section');
        },
    });

    const createItemMutation = useMutation({
        mutationFn: (data: ItemFormData & { costSectionId: string }) => {
            const payload = {
                ...data,
                defaultAmount: data.defaultAmount ?? undefined,
                defaultPercentage: data.defaultPercentage ?? undefined,
                defaultUnitPrice: data.defaultUnitPrice ?? undefined,
            };
            return costItemsApi.create(payload as any);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-items'] });
            toast.success('Item created successfully');
            setIsItemModalOpen(false);
            setEditingItem(null);
            resetItem({
                name: '',
                unit: 'PER_PROCEDURE',
                isSelectable: false,
                exclusiveGroup: '',
                defaultAmount: null,
                defaultPercentage: null,
                defaultUnitPrice: null,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create item');
        },
    });

    const updateItemMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ItemFormData> }) => {
            const payload = {
                ...data,
                defaultAmount: data.defaultAmount ?? undefined,
                defaultPercentage: data.defaultPercentage ?? undefined,
                defaultUnitPrice: data.defaultUnitPrice ?? undefined,
            };
            return costItemsApi.update(id, payload as any);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-items'] });
            toast.success('Item updated successfully');
            setIsItemModalOpen(false);
            setEditingItem(null);
            resetItem({
                name: '',
                unit: 'PER_PROCEDURE',
                isSelectable: false,
                exclusiveGroup: '',
                defaultAmount: null,
                defaultPercentage: null,
                defaultUnitPrice: null,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update item');
        },
    });

    const deleteItemMutation = useMutation({
        mutationFn: costItemsApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-items'] });
            toast.success('Item deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to delete item');
        },
    });

    const {
        register: registerSection,
        handleSubmit: handleSubmitSection,
        formState: { errors: sectionErrors },
        reset: resetSection,
    } = useForm<SectionFormData>({
        resolver: zodResolver(sectionSchema),
        defaultValues: {
            name: '',
            sectionType: 'FIXED',
            isRequired: true,
        },
    });

    const {
        register: registerItem,
        handleSubmit: handleSubmitItem,
        formState: { errors: itemErrors },
        reset: resetItem,
        watch: watchItem,
    } = useForm<ItemFormData>({
        resolver: zodResolver(itemSchema),
        defaultValues: {
            name: '',
            unit: 'PER_PROCEDURE',
            isSelectable: false,
            exclusiveGroup: '',
            defaultAmount: null,
            defaultPercentage: null,
            defaultUnitPrice: null,
        },
    });

    const onSubmitSection = (data: SectionFormData) => {
        if (editingSection) {
            updateSectionMutation.mutate({ id: editingSection.id, data });
        } else {
            createSectionMutation.mutate({ ...data, locationId: selectedLocationId });
        }
    };

    const onSubmitItem = (data: ItemFormData) => {
        const payload = {
            ...data,
            defaultAmount: data.defaultAmount ?? undefined,
            defaultPercentage: data.defaultPercentage ?? undefined,
            defaultUnitPrice: data.defaultUnitPrice ?? undefined,
        };

        if (editingItem) {
            updateItemMutation.mutate({ id: editingItem.id, data: payload });
        } else {
            createItemMutation.mutate({ ...payload, costSectionId: selectedSectionId });
        }
    };

    const handleEditSection = (section: any) => {
        setEditingSection(section);
        resetSection(section);
        setIsSectionModalOpen(true);
    };

    const handleEditItem = (item: any) => {
        setEditingItem(item);
        // Ensure defaults are null if undefined to match form schema
        resetItem({
            ...item,
            defaultAmount: item.defaultAmount || null,
            defaultPercentage: item.defaultPercentage || null,
            defaultUnitPrice: item.defaultUnitPrice || null,
        });
        setIsItemModalOpen(true);
    };

    // Auto-select first location if available
    if (locations.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locations[0].id);
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Cost Configuration</h1>
                <p className="text-slate-600">Manage cost sections and items for estimates</p>
            </div>

            {/* Location Selector */}
            <div className="card">
                <div className="card-body">
                    <label className="label">Select Location</label>
                    <select
                        className="input max-w-md"
                        value={selectedLocationId}
                        onChange={(e) => {
                            setSelectedLocationId(e.target.value);
                            setSelectedSectionId('');
                        }}
                    >
                        <option value="">Choose a location...</option>
                        {locations.map((location: any) => (
                            <option key={location.id} value={location.id}>
                                {location.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedLocationId && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cost Sections */}
                    <div className="card">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Cost Sections</h2>
                            <button
                                onClick={() => setIsSectionModalOpen(true)}
                                className="btn btn-primary btn-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Add Section
                            </button>
                        </div>

                        <div className="card-body">
                            {sectionsLoading ? (
                                <div className="space-y-2">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                    ))}
                                </div>
                            ) : sections.length === 0 ? (
                                <div className="text-center py-8">
                                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                    <p className="text-slate-500">No sections yet</p>
                                    <button
                                        onClick={() => setIsSectionModalOpen(true)}
                                        className="btn btn-primary btn-sm mt-2"
                                    >
                                        Add First Section
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {sections.map((section: any) => (
                                        <div
                                            key={section.id}
                                            className={`p-3 border rounded-lg transition-colors cursor-pointer ${selectedSectionId === section.id
                                                ? 'border-primary-500 bg-primary-50'
                                                : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                            onClick={() => setSelectedSectionId(section.id)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <GripVertical className="w-4 h-4 text-slate-400" />
                                                        <h3 className="font-medium text-slate-900">{section.name}</h3>
                                                    </div>
                                                    <div className="flex gap-2 mt-1">
                                                        <span
                                                            className={`text-xs px-2 py-0.5 rounded ${section.sectionType === 'FIXED'
                                                                ? 'bg-blue-100 text-blue-700'
                                                                : 'bg-purple-100 text-purple-700'
                                                                }`}
                                                        >
                                                            {section.sectionType}
                                                        </span>
                                                        {section.isRequired && (
                                                            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                                                                Required
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditSection(section);
                                                        }}
                                                        className="p-1 hover:bg-white rounded"
                                                    >
                                                        <Edit2 className="w-4 h-4 text-slate-500" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (confirm('Delete this section?')) {
                                                                deleteSectionMutation.mutate(section.id);
                                                            }
                                                        }}
                                                        className="p-1 hover:bg-white rounded"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-error-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cost Items */}
                    <div className="card">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {selectedSectionId
                                    ? `Items in ${sections.find((s: any) => s.id === selectedSectionId)?.name}`
                                    : 'Cost Items'}
                            </h2>
                            {selectedSectionId && (
                                <button
                                    onClick={() => setIsItemModalOpen(true)}
                                    className="btn btn-primary btn-sm"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Item
                                </button>
                            )}
                        </div>

                        <div className="card-body">
                            {!selectedSectionId ? (
                                <div className="text-center py-8 text-slate-500">
                                    Select a section to view items
                                </div>
                            ) : itemsLoading ? (
                                <div className="space-y-2">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-slate-500">No items yet</p>
                                    <button
                                        onClick={() => setIsItemModalOpen(true)}
                                        className="btn btn-primary btn-sm mt-2"
                                    >
                                        Add First Item
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {items.map((item: any) => (
                                        <div
                                            key={item.id}
                                            className="p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-slate-900">{item.name}</h4>
                                                    <div className="flex gap-2 mt-1">
                                                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                                                            {item.unit.replace('_', ' ')}
                                                        </span>
                                                        {item.exclusiveGroup && (
                                                            <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700">
                                                                Group: {item.exclusiveGroup}
                                                            </span>
                                                        )}
                                                        {!item.isSelectable && (
                                                            <span className="text-xs px-2 py-0.5 rounded bg-warning-50 text-warning-700">
                                                                Auto-include
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleEditItem(item)}
                                                        className="p-1 hover:bg-slate-100 rounded"
                                                    >
                                                        <Edit2 className="w-4 h-4 text-slate-500" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            if (confirm('Delete this item?')) {
                                                                deleteItemMutation.mutate(item.id);
                                                            }
                                                        }}
                                                        className="p-1 hover:bg-slate-100 rounded"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-error-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Section Modal */}
            {isSectionModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-md">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingSection ? 'Edit Section' : 'Add Section'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsSectionModalOpen(false);
                                    setEditingSection(null);
                                    resetSection({
                                        name: '',
                                        sectionType: 'FIXED',
                                        isRequired: true,
                                    });
                                }}
                                className="p-1 hover:bg-slate-100 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitSection(onSubmitSection)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Section Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${sectionErrors.name ? 'input-error' : ''}`}
                                        placeholder="e.g., Room Charges"
                                        {...registerSection('name')}
                                    />
                                    {sectionErrors.name && (
                                        <p className="error-text">{sectionErrors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Type *</label>
                                    <select
                                        className={`input ${sectionErrors.sectionType ? 'input-error' : ''}`}
                                        {...registerSection('sectionType')}
                                    >
                                        <option value="FIXED">Fixed Amount</option>
                                        <option value="PERCENTAGE">Percentage</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isRequired"
                                        className="w-4 h-4"
                                        {...registerSection('isRequired')}
                                    />
                                    <label htmlFor="isRequired" className="text-sm text-slate-700">
                                        Required section (always included)
                                    </label>
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSectionModalOpen(false);
                                        setEditingSection(null);
                                        resetSection({
                                            name: '',
                                            sectionType: 'FIXED',
                                            isRequired: true,
                                        });
                                    }}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createSectionMutation.isPending || updateSectionMutation.isPending
                                        ? 'Saving...'
                                        : editingSection
                                            ? 'Update Section'
                                            : 'Add Section'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Item Modal */}
            {isItemModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-md">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingItem ? 'Edit Item' : 'Add Item'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsItemModalOpen(false);
                                    setEditingItem(null);
                                    resetItem({
                                        name: '',
                                        unit: 'PER_PROCEDURE',
                                        isSelectable: false,
                                        exclusiveGroup: '',
                                    });
                                }}
                                className="p-1 hover:bg-slate-100 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitItem(onSubmitItem)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Item Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${itemErrors.name ? 'input-error' : ''}`}
                                        placeholder="e.g., General Ward"
                                        {...registerItem('name')}
                                    />
                                    {itemErrors.name && <p className="error-text">{itemErrors.name.message}</p>}
                                </div>

                                <div>
                                    <label className="label">Unit *</label>
                                    <select
                                        className={`input ${itemErrors.unit ? 'input-error' : ''}`}
                                        {...registerItem('unit')}
                                    >
                                        <option value="PER_DAY">Per Day</option>
                                        <option value="PER_PROCEDURE">Per Procedure</option>
                                        <option value="PER_UNIT">Per Unit</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Exclusive Group (optional)</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="e.g., room_type"
                                        {...registerItem('exclusiveGroup')}
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        Items in the same group are mutually exclusive
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isSelectable"
                                        className="w-4 h-4"
                                        {...registerItem('isSelectable')}
                                    />
                                    <label htmlFor="isSelectable" className="text-sm text-slate-700">
                                        User can select this item
                                    </label>
                                </div>

                                {/* Default Pricing Configuration */}
                                <div className="border-t border-slate-200 pt-4 mt-2">
                                    <h4 className="text-sm font-medium text-slate-900 mb-3">Default Pricing (Location Level)</h4>

                                    {watchItem('unit') === 'PER_UNIT' && (
                                        <div>
                                            <label className="label">Base Unit Price *</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500">₹</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className={`input ${itemErrors.defaultUnitPrice ? 'input-error' : ''}`}
                                                    placeholder="0.00"
                                                    {...registerItem('defaultUnitPrice', { valueAsNumber: true })}
                                                />
                                                <span className="text-xs text-slate-500">per unit</span>
                                            </div>
                                            {itemErrors.defaultUnitPrice && (
                                                <p className="error-text">{itemErrors.defaultUnitPrice.message}</p>
                                            )}
                                            <p className="text-xs text-slate-500 mt-1">
                                                Example: OT Charges = ₹5000 / hour
                                            </p>
                                        </div>
                                    )}

                                    {watchItem('unit') === 'PER_DAY' && (
                                        <div>
                                            <label className="label">Default Daily Rate</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500">₹</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="input"
                                                    placeholder="0.00"
                                                    {...registerItem('defaultAmount', { valueAsNumber: true })}
                                                />
                                                <span className="text-xs text-slate-500">per day</span>
                                            </div>
                                        </div>
                                    )}

                                    {watchItem('unit') === 'PER_PROCEDURE' && sections.find((s: any) => s.id === selectedSectionId)?.sectionType === 'FIXED' && (
                                        <div>
                                            <label className="label">Default Amount</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500">₹</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="input"
                                                    placeholder="0.00"
                                                    {...registerItem('defaultAmount', { valueAsNumber: true })}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {sections.find((s: any) => s.id === selectedSectionId)?.sectionType === 'PERCENTAGE' && (
                                        <div>
                                            <label className="label">Default Percentage</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="input"
                                                    placeholder="0.00"
                                                    {...registerItem('defaultPercentage', { valueAsNumber: true })}
                                                />
                                                <span className="text-slate-500">%</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsItemModalOpen(false);
                                        setEditingItem(null);
                                        resetItem({
                                            name: '',
                                            unit: 'PER_PROCEDURE',
                                            isSelectable: false,
                                            exclusiveGroup: '',
                                            defaultAmount: null,
                                            defaultPercentage: null,
                                            defaultUnitPrice: null,
                                        });
                                    }}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createItemMutation.isPending || updateItemMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createItemMutation.isPending || updateItemMutation.isPending
                                        ? 'Saving...'
                                        : editingItem
                                            ? 'Update Item'
                                            : 'Add Item'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
